/**
 * BenchmarkToolInventory — the option set for the benchmark Start form's
 * "tools disabled" multi-select (DEV-17197).
 *
 * A Start arm's `profile.disabledTools` is the NEGATIVE tools lever: each
 * selected id is withheld from the launched threads. This payload is what
 * the form enumerates so the operator can pick from the tools the group's
 * ORCHESTRATION definitions can actually reach, grouped by MCP connection
 * (then platform tools last).
 *
 * `disabledByDefault` is the prefill, not a recommendation. It is true when
 * NO currently-configured definition exposes that tool — i.e. the
 * definitions' existing `toolAllowlists` already withhold it — so the
 * operator sees the group's current restrictions rather than a blank slate.
 * Selecting a prefilled id in the form is a no-op relative to baseline;
 * clearing it would be a widening the Start path does not currently offer
 * (widening is a definition edit). Canonical ids match `disabledTools`
 * (`"<connectionName>/<toolName>"` for an MCP tool, `"<ref>"` for a
 * platform tool id).
 */
import { z } from 'zod';
export const BenchmarkToolInventoryToolSchema = z
    .object({
    /** Canonical `disabledTools` id (`connection/tool` for MCP, bare ref for platform). */
    id: z.string().min(1),
    /** Bare tool name (MCP) or platform tool id. */
    name: z.string().min(1),
    description: z.string().nullable(),
    /** Formant tool-group taxonomy from the MCP `_meta.group`, when present. */
    group: z.string().nullable(),
    /**
     * Already withheld by the definitions' `toolAllowlists` — the Start
     * form's prefill. Platform refs have no partial state (referenced or
     * absent) so they are always `false`.
     */
    disabledByDefault: z.boolean(),
})
    .strict();
export const BenchmarkToolInventoryGroupSchema = z
    .object({
    kind: z.enum(['mcp', 'platform']),
    /** MCP connection name, or `'platform'` for the platform-tool group. */
    ref: z.string().min(1),
    /** Display label for the optgroup / section header. */
    label: z.string().min(1),
    /**
     * MCP only: false when no tool inventory could be produced (cache miss
     * AND the live `tools/list` probe failed). Platform groups are always
     * resolved (the registry is local).
     */
    resolved: z.boolean(),
    tools: z.array(BenchmarkToolInventoryToolSchema),
})
    .strict();
export const BenchmarkToolInventorySchema = z
    .object({
    groups: z.array(BenchmarkToolInventoryGroupSchema),
})
    .strict();
//# sourceMappingURL=benchmark-tool-inventory.js.map